var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import cn from 'classnames';
import mergeRefs from 'merge-refs';
import { forwardRef, useCallback, useEffect, useRef, useState, } from 'react';
import { InputPrivate } from '@snack-uikit/input-private';
import { Droplist } from '@snack-uikit/list';
import { extractSupportProps, isBrowser, useLayoutEffect } from '@snack-uikit/utils';
import { FieldContainerPrivate } from '../../helperComponents';
import { usePostfix, usePrefix, useValueControl } from '../../hooks';
import { getValidationState } from '../../utils/getValidationState';
import { FieldDecorator } from '../FieldDecorator';
import { extractFieldDecoratorProps } from '../FieldDecorator/utils';
import { useButtons, useHandleOnKeyDown, useSearch, useSearchInput } from './hooks';
import styles from './styles.module.css';
import { checkisSearchUnavailable, extractListProps, getArrowIcon, updateItems } from './utils';
const defaultSelectedOptionFormatter = item => 
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error
(item === null || item === void 0 ? void 0 : item.content.option) || '';
export const FieldSelectSingle = forwardRef((props, ref) => {
    const { id, name, placeholder, size = 's', options, value: valueProp, defaultValue, onChange: onChangeProp, disabled = false, readonly = false, searchable = true, showCopyButton = true, showClearButton = true, onKeyDown: onInputKeyDownProp, required = false, validationState = 'default', search, autocomplete = false, prefixIcon, prefix, postfix, addOptionByEnter = false, untouchableScrollbars = false, open: openProp, onOpenChange, selectedOptionFormatter = defaultSelectedOptionFormatter, enableFuzzySearch = true, onCopyButtonClick } = props, rest = __rest(props, ["id", "name", "placeholder", "size", "options", "value", "defaultValue", "onChange", "disabled", "readonly", "searchable", "showCopyButton", "showClearButton", "onKeyDown", "required", "validationState", "search", "autocomplete", "prefixIcon", "prefix", "postfix", "addOptionByEnter", "untouchableScrollbars", "open", "onOpenChange", "selectedOptionFormatter", "enableFuzzySearch", "onCopyButtonClick"]);
    const localRef = useRef(null);
    const [open = false, setOpen] = useValueControl({ value: openProp, onChange: onOpenChange });
    const [value, setValue] = useValueControl({
        value: valueProp,
        defaultValue,
        onChange: onChangeProp,
    });
    const [{ selectedItem, items = [] }, setItems] = useState(() => updateItems({ options, value, currentItems: [], selectedItem: undefined }));
    const { inputValue, setInputValue, prevInputValue, updateInputValue } = useSearchInput(Object.assign(Object.assign({}, search), { defaultValue: selectedOptionFormatter(selectedItem), selectedOptionFormatter }));
    const prevSelectedItem = useRef(selectedItem);
    const prefixSettings = usePrefix({ prefix, disabled });
    const postfixSettings = usePostfix({ postfix, disabled });
    useLayoutEffect(() => {
        setItems(({ selectedItem }) => updateItems({ options, value, selectedItem }));
    }, [options, value]);
    useEffect(() => {
        if (prevSelectedItem.current &&
            prevSelectedItem.current.id === (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.id) &&
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            prevSelectedItem.current.content.option === (selectedItem === null || selectedItem === void 0 ? void 0 : selectedItem.content.option)) {
            return;
        }
        prevSelectedItem.current = selectedItem;
        updateInputValue(selectedItem);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem, prevSelectedItem]);
    const onClear = useCallback(() => {
        var _a;
        setValue(undefined);
        (_a = localRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        if (required) {
            setOpen(true);
        }
    }, [required, setOpen, setValue]);
    const { ArrowIcon, arrowIconSize } = getArrowIcon({ size, open });
    const { postfixButtons, inputKeyDownNavigationHandler, buttonsRefs } = useButtons({
        readonly,
        size,
        showClearButton: showClearButton && !disabled && !readonly && value !== undefined,
        showCopyButton,
        inputRef: localRef,
        onClear,
        onCopyButtonClick,
        valueToCopy: selectedOptionFormatter(selectedItem),
    });
    const handleBlur = (e) => {
        var _a;
        if (!open && !buttonsRefs.filter(Boolean).includes(e.relatedTarget)) {
            updateInputValue(selectedItem);
            (_a = rest === null || rest === void 0 ? void 0 : rest.onBlur) === null || _a === void 0 ? void 0 : _a.call(rest, e);
        }
    };
    const commonHandleOnKeyDown = useHandleOnKeyDown({
        inputKeyDownNavigationHandler,
        onInputKeyDownProp,
        setOpen,
    });
    const handleSelectionChange = useCallback((newValue) => {
        var _a;
        if (newValue !== undefined) {
            (_a = localRef.current) === null || _a === void 0 ? void 0 : _a.focus();
            setOpen(false);
            setValue(newValue);
        }
    }, [setOpen, setValue]);
    const handleOnKeyDown = (onKeyDown) => (e) => {
        if (!open && prevInputValue.current !== inputValue) {
            setOpen(true);
        }
        if (e.code === 'Enter') {
            e.stopPropagation();
            e.preventDefault();
        }
        if (addOptionByEnter && e.code === 'Enter' && inputValue !== '') {
            handleSelectionChange(inputValue);
        }
        commonHandleOnKeyDown(onKeyDown)(e);
    };
    const handleOpenChange = (open) => {
        if (isBrowser() && !readonly && !disabled && !buttonsRefs.includes(document.activeElement)) {
            setOpen(open);
            if (!open) {
                updateInputValue(selectedItem);
            }
        }
    };
    const filterFunction = useSearch(items, enableFuzzySearch);
    const isSearchUnavailable = checkisSearchUnavailable({
        autocomplete,
        searchable,
        isSameValue: selectedOptionFormatter(selectedItem) === inputValue,
    });
    const result = isSearchUnavailable ? items : filterFunction(inputValue);
    const fieldValidationState = getValidationState({ validationState, error: rest.error });
    return (_jsx(FieldDecorator, Object.assign({}, extractSupportProps(rest), extractFieldDecoratorProps(props), { validationState: fieldValidationState, children: _jsx(Droplist, Object.assign({}, extractListProps(props), { items: result, selection: {
                mode: 'single',
                value: value,
                onChange: handleSelectionChange,
            }, size: size, open: open, onOpenChange: handleOpenChange, trigger: 'click', triggerElemRef: localRef, untouchableScrollbars: untouchableScrollbars, children: ({ onKeyDown }) => (_jsxs(FieldContainerPrivate, { className: styles.container, validationState: fieldValidationState, disabled: disabled, readonly: readonly, focused: open, variant: 'single-line-container', inputRef: localRef, size: size, prefix: (prefixIcon || prefixSettings.show) && (_jsxs(_Fragment, { children: [prefixIcon, prefixSettings.show && prefixSettings.render({ key: prefixSettings.id })] })), children: [_jsx(InputPrivate, { id: id, name: name, type: 'text', disabled: disabled, placeholder: placeholder, ref: mergeRefs(ref, localRef), onChange: searchable ? setInputValue : undefined, value: searchable ? inputValue : selectedOptionFormatter(selectedItem), readonly: readonly, "data-test-id": 'field-select__input', onKeyDown: handleOnKeyDown(onKeyDown), onBlur: handleBlur, className: cn({
                            [styles.readonlyCursor]: !searchable,
                        }) }), _jsxs("div", { className: styles.postfix, children: [postfixButtons, postfixSettings.show && postfixSettings.render({ key: postfixSettings.id }), _jsx(ArrowIcon, { size: arrowIconSize, className: styles.arrowIcon })] })] })) })) })));
});
